import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { getLocalStorageInfo, getObjectSize } from '../utils/storageUtils';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  color: #fff;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 600;
  margin-bottom: 2rem;
  color: #fff;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 25px;
  margin-bottom: 30px;
`;

const CardTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 20px;
  color: #fff;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 24px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
  margin: 20px 0;
`;

const ProgressFill = styled.div`
  height: 100%;
  background-color: ${props => props.critical ? '#ef4444' : props.warning ? '#f59e0b' : '#22c55e'};
  width: ${props => props.percentage}%;
  transition: width 0.5s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  &:last-child {
    border-bottom: none;
  }
`;

const Button = styled.button`
  background-color: #b573ff;
  color: white;
  padding: 10px 20px;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  margin-right: 10px;
  
  &:hover {
    background-color: #9b5ed9;
  }
  
  &:disabled {
    background-color: #666;
    cursor: not-allowed;
  }
`;

const StorageDiagnosticsPage = () => {
    const [storageInfo, setStorageInfo] = useState(null);
    const [cartInfo, setCartInfo] = useState({ items: [], totalSize: 0 });
    const [loading, setLoading] = useState(true);
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        const fetchStorageInfo = () => {
            setLoading(true);

            try {
                // Get localStorage info
                const info = getLocalStorageInfo();
                setStorageInfo(info);

                // Get cart info
                const cart = JSON.parse(localStorage.getItem('cart') || '[]');
                const cartSize = getObjectSize(cart);

                // Get size of individual items
                const itemsWithSize = cart.map(item => {
                    const size = getObjectSize(item);
                    const imageSize = item.imageUrl ?
                        (item.imageUrl.startsWith('data:') ? item.imageUrl.length * 2 / 1024 : 0) : 0;

                    return {
                        ...item,
                        size,
                        sizeKB: (size / 1024).toFixed(2),
                        imageSize,
                        imageSizeKB: (imageSize / 1024).toFixed(2)
                    };
                });

                setCartInfo({
                    items: itemsWithSize,
                    totalSize: cartSize,
                    totalSizeKB: (cartSize / 1024).toFixed(2),
                    count: cart.length
                });
            } catch (error) {
                console.error('Error fetching storage info:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchStorageInfo();
    }, [refreshKey]);

    const handleRefresh = () => {
        setRefreshKey(prev => prev + 1);
    };

    const handleClearCart = () => {
        if (window.confirm('Are you sure you want to clear your cart?')) {
            localStorage.setItem('cart', '[]');
            window.dispatchEvent(new Event('cartUpdated'));
            handleRefresh();
        }
    };

    const handleOptimizeCart = () => {
        try {
            const cart = JSON.parse(localStorage.getItem('cart') || '[]');

            // Replace any data URLs with references
            const optimizedCart = cart.map(item => {
                if (item.imageUrl && item.imageUrl.startsWith('data:')) {
                    return {
                        ...item,
                        imageUrl: '/images/products/default-map.jpg',
                        _imageOptimized: true
                    };
                }
                return item;
            });

            localStorage.setItem('cart', JSON.stringify(optimizedCart));
            window.dispatchEvent(new Event('cartUpdated'));
            handleRefresh();

        } catch (error) {
            console.error('Error optimizing cart:', error);
        }
    };

    const handleRestoreOptimizedImages = () => {
        try {
            const cart = JSON.parse(localStorage.getItem('cart') || '[]');

            // Check if any items were optimized
            const hasOptimizedItems = cart.some(item => item._imageOptimized);

            if (!hasOptimizedItems) {
                alert('No optimized images found in cart.');
                return;
            }

            if (window.confirm('This will attempt to restore original star map images. This might cause storage issues if the images are large. Continue?')) {
                // For each optimized item, we'll need to regenerate the image
                // In a real implementation, you might want to fetch these from a server
                // For now, we'll just remove the optimization flag
                const restoredCart = cart.map(item => {
                    if (item._imageOptimized) {
                        // In a real app, you would regenerate or fetch the image here
                        // For now, we'll just mark it as not optimized
                        const restored = { ...item };
                        delete restored._imageOptimized;

                        // If we had a way to regenerate the image, we'd do it here
                        // For now, we'll keep using the default image but remove the flag

                        return restored;
                    }
                    return item;
                });

                localStorage.setItem('cart', JSON.stringify(restoredCart));
                window.dispatchEvent(new Event('cartUpdated'));
                handleRefresh();
                alert('Optimization flags removed. Note: actual images could not be restored as they were not saved.');
            }
        } catch (error) {
            console.error('Error restoring images:', error);
            alert('Error restoring images: ' + error.message);
        }
    };

    if (loading) {
        return (
            <PageContainer>
                <PageTitle>Storage Diagnostics</PageTitle>
                <p>Loading storage information...</p>
            </PageContainer>
        );
    }

    const usagePercentage = storageInfo ?
        Math.round((storageInfo.used / storageInfo.total) * 100) : 0;

    const cartPercentage = storageInfo && cartInfo ?
        Math.round((cartInfo.totalSize / storageInfo.total) * 100) : 0;

    return (
        <PageContainer>
            <PageTitle>Storage Diagnostics</PageTitle>

            <Card>
                <CardTitle>LocalStorage Usage</CardTitle>
                <ProgressBar>
                    <ProgressFill
                        percentage={usagePercentage}
                        warning={usagePercentage > 70}
                        critical={usagePercentage > 90}
                    >
                        {usagePercentage}%
                    </ProgressFill>
                </ProgressBar>

                <InfoRow>
                    <div>Used Space:</div>
                    <div>{storageInfo?.usedMB} MB</div>
                </InfoRow>
                <InfoRow>
                    <div>Available Space:</div>
                    <div>{storageInfo?.availableMB} MB</div>
                </InfoRow>
                <InfoRow>
                    <div>Total Space:</div>
                    <div>{storageInfo?.totalMB} MB</div>
                </InfoRow>
            </Card>

            <Card>
                <CardTitle>Cart Storage</CardTitle>
                <ProgressBar>
                    <ProgressFill
                        percentage={cartPercentage}
                        warning={cartPercentage > 50}
                        critical={cartPercentage > 80}
                    >
                        {cartPercentage}%
                    </ProgressFill>
                </ProgressBar>

                <InfoRow>
                    <div>Cart Size:</div>
                    <div>{cartInfo?.totalSizeKB} KB</div>
                </InfoRow>
                <InfoRow>
                    <div>Items in Cart:</div>
                    <div>{cartInfo?.count}</div>
                </InfoRow>
                <InfoRow>
                    <div>Percentage of Total Storage:</div>
                    <div>{cartPercentage}%</div>
                </InfoRow>

                <div style={{ marginTop: '20px' }}>
                    <Button onClick={handleOptimizeCart} disabled={!cartInfo.items.length}>
                        Optimize Cart
                    </Button>
                    <Button onClick={handleClearCart} disabled={!cartInfo.items.length}>
                        Clear Cart
                    </Button>
                    <Button
                        onClick={handleRestoreOptimizedImages}
                        disabled={!cartInfo.items.some(item => item._imageOptimized)}
                        style={{ marginLeft: '10px' }}
                    >
                        Restore Images
                    </Button>
                </div>
            </Card>

            {cartInfo.items.length > 0 && (
                <Card>
                    <CardTitle>Cart Items</CardTitle>
                    {cartInfo.items.map((item, index) => (
                        <Card key={item.id || index} style={{ marginBottom: '10px', backgroundColor: 'rgba(0,0,0,0.2)' }}>
                            <InfoRow>
                                <div>Item:</div>
                                <div>{item.name || 'Unnamed Item'}</div>
                            </InfoRow>
                            <InfoRow>
                                <div>Size:</div>
                                <div>{item.sizeKB} KB</div>
                            </InfoRow>
                            {item.imageUrl && (
                                <InfoRow>
                                    <div>Image Type:</div>
                                    <div>
                                        {item.imageUrl.startsWith('data:') ?
                                            'Data URL (large)' :
                                            item._imageOptimized ?
                                                'Optimized Reference' : 'Image Reference'}
                                    </div>
                                </InfoRow>
                            )}
                            {item.imageUrl && item.imageUrl.startsWith('data:') && (
                                <InfoRow>
                                    <div>Image Size:</div>
                                    <div>{item.imageSizeKB} KB</div>
                                </InfoRow>
                            )}
                        </Card>
                    ))}
                </Card>
            )}

            <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <Button onClick={handleRefresh}>
                    Refresh Data
                </Button>
            </div>
        </PageContainer>
    );
};

export default StorageDiagnosticsPage; 